import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Play, Square, RefreshCw, Check, Loader2, X } from 'lucide-react'
import { api, blobURL } from '../api'
import type { JobDetail as Detail, JobEvent, Job, Scene, Role } from '../types'
import { atLeast } from '../types'
import { go } from '../router'
import {
  Button, Card, ErrorText, Note, SectionLabel, Skeleton, StatusBadge, Badge, toneFor,
} from '../components/ui'
import { DownloadFileButton } from '../components'
import { usd, cn, modelName } from '../lib/utils'

const RUNNING = new Set(['queued', 'generating'])

export function JobDetail({ ws, id, role }: { ws: string; id: string; role?: Role }) {
  const qc = useQueryClient()
  const base = `/api/workspaces/${ws}/jobs/${id}`

  const detail = useQuery({
    queryKey: ['job', ws, id],
    queryFn: () => api.get<Detail>(base),
    refetchInterval: q =>
      RUNNING.has((q.state.data as Detail | undefined)?.job.status ?? '') ? 3000 : false,
  })
  const job = detail.data?.job

  const events = useQuery({
    queryKey: ['job-events', ws, id],
    queryFn: () => api.get<JobEvent[]>(`${base}/events`),
	// Scene generations finish independently. Poll the lightweight event list
	// every second while active so a parallel scene gets its check mark promptly
	// instead of appearing stuck for the detail query's longer interval.
	refetchInterval: RUNNING.has(job?.status ?? '') ? 1000 : false,
  })

  const queue = useMutation({
    mutationFn: () => api.post<{ estimate_usd: number; warnings: string[] | null }>(`${base}/queue`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['job', ws, id] }),
  })
  const cancel = useMutation({
    mutationFn: () => api.post(`${base}/cancel`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['job', ws, id] }),
  })

  if (detail.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-40 w-full" />
      </div>
    )
  }
  if (!job) return <ErrorText error={detail.error} />

  const canRun = atLeast(role, 'member')
  const runnable = ['draft', 'failed', 'aborted'].includes(job.status)
  const live = RUNNING.has(job.status)

  return (
    <div className="space-y-6">
      <Button variant="ghost" size="sm" className="-ml-3" onClick={() => go({ name: 'jobs' })}>
        <ArrowLeft /> Videos
      </Button>

      <div className="flex min-w-0 items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <h1
            className="truncate text-2xl"
            title={job.pain_point || 'Untitled video'}
          >
            {job.pain_point || 'Untitled video'}
          </h1>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-ink-faint">
            <StatusBadge status={job.status} />
            {job.video_model && <><span>·</span><span>{modelName(job.video_model)}</span></>}
            <span>·</span>
            <span className="tabular-nums">{usd(job.cost_cents)} spent</span>
            {job.stage && <><span>·</span><span>{job.stage}</span></>}
          </div>
        </div>

        <div className="flex shrink-0 gap-2">
		  {canRun && job.output_key && (
			<Button variant="secondary" onClick={() => go({ name: 'new-job', sourceID: job.id, regenerationMode: 'scenes' })}>
			  <RefreshCw /> Regenerate
            </Button>
          )}
          {canRun && runnable && (
            <Button variant="primary" onClick={() => queue.mutate()} disabled={queue.isPending}>
              {job.status === 'draft' ? <><Play /> Generate</> : <><RefreshCw /> Retry</>}
            </Button>
          )}
          {canRun && live && (
            <Button
              variant="secondary"
              onClick={() => cancel.mutate()}
              disabled={cancel.isPending || job.cancel_requested}
            >
              <Square /> {job.cancel_requested ? 'Stopping…' : 'Stop'}
            </Button>
          )}
        </div>
      </div>

      <ErrorText error={queue.error} />

      {queue.data && (
        <Note tone="brand">
          Queued — estimated ${queue.data.estimate_usd.toFixed(2)} at first take.
          {queue.data.warnings?.map(w => <div key={w} className="mt-1 text-warn">{w}</div>)}
        </Note>
      )}

      {job.cancel_requested && live && (
        <Note tone="warn">
		  Stopping after generations already in flight finish. They have already been
		  submitted and paid for, so their clips will be kept for a retry.
        </Note>
      )}

      {job.error && <ErrorText error={job.error} />}

      {job.output_key && (
        <Result ws={ws} storageKey={job.output_key} filename={`reelworks-video-${job.id}.mp4`} />
      )}

      <section className="space-y-3">
        <SectionLabel>Scenes</SectionLabel>
        <div className="space-y-2">
          {detail.data!.scenes.map(s => {
            const takes = detail.data!.takes[s.id] ?? []
            return (
              <Card key={s.id} className="flex gap-4 p-4">
                <div className="grid size-7 shrink-0 place-items-center rounded-full bg-surface-2 text-xs font-medium text-ink-dim">
                  {s.position}
                </div>
                <div className="min-w-0 flex-1 space-y-2">
                  <p className={cn('text-sm', !s.line && 'text-ink-faint')}>
                    {s.line ?? 'Product demo'}
                  </p>
                  <div className="flex flex-wrap items-center gap-1.5">
                    {s.chain_from && <Badge>continues previous</Badge>}
                    {takes.length === 0 && <span className="text-xs text-ink-faint">No takes yet</span>}
                    {takes.map(t => (
                      <Badge key={t.id} tone={t.accepted ? 'good' : toneFor(t.qc_status ?? '')}>
                        Take {t.take_number} · {t.accepted ? 'accepted' : t.qc_status ?? 'pending'}
                      </Badge>
                    ))}
                  </div>
                </div>
              </Card>
            )
          })}
        </div>
      </section>

      <section className="space-y-3">
        <SectionLabel>Run log</SectionLabel>
        <Stages job={job} scenes={detail.data?.scenes ?? []} events={events.data ?? []} />
        <Log events={events.data ?? []} live={live} />
      </section>
    </div>
  )
}

/** What the run is made of, ticked off as it goes.
 *
 *  The log alone answers "what is it doing" but never "how much is left" -- it
 *  is a stream of prose with no shape. The stages are knowable before the run
 *  starts: one per scene, then assemble, captions and ambience. Listing them up
 *  front turns a wall of text into a progress bar you can read. */
function Stages({ job, scenes, events }: { job: Job; scenes: Scene[]; events: JobEvent[] }) {
  const names = [
    ...scenes.map((_, i) => `scene ${i + 1}`),
    'assemble',
    'captions',
    'ambience',
  ]
  const labels: Record<string, string> = {
    assemble: 'Assemble the cut',
    captions: 'Burn captions',
    ambience: 'Add room tone',
  }

  const finished = job.status === 'review' || job.status === 'approved'
  const stopped = job.status === 'failed' || job.status === 'aborted'
  // Everything before the current stage is done. The stage is set when a step
  // BEGINS, so its own position is the one in flight.
  const at = job.stage ? names.indexOf(job.stage) : -1
  const parallel = new Set<number>()
	const parallelFinished = new Set<number>()
  for (const event of events) {
    const match = event.stage?.match(/^scene (\d+)$/)
    if (!match) continue
    const scene = Number(match[1])
    if (event.message === 'Generating in parallel') parallel.add(scene)
	if (event.message === 'Parallel generation finished') {
	  parallel.delete(scene)
	  parallelFinished.add(scene)
	}
  }

  return (
    <div className="space-y-2">
      <SectionLabel>Steps</SectionLabel>
      <div className="space-y-1">
        {names.map((name, i) => {
          const current = !finished && (at === i || parallel.has(i + 1))
		  const done = finished || parallelFinished.has(i + 1) || (at >= 0 && i < at && !current)
          const failed = stopped && at === i
          const scene = i < scenes.length ? scenes[i] : undefined
          return (
            <div key={name} className="flex items-center gap-2.5 text-sm">
              <span className="grid size-5 shrink-0 place-items-center">
                {failed ? <X className="size-4 text-bad" />
                  : done ? <Check className="size-4 text-good" />
                    : current ? <Loader2 className="size-4 animate-spin text-brand" />
                      : <span className="size-1.5 rounded-full bg-line-strong" />}
              </span>
              <span className={cn('min-w-0 truncate',
                failed ? 'text-bad' : done ? 'text-ink-dim' : current ? 'text-ink' : 'text-ink-faint')}>
                {labels[name] ?? (
                  scene?.kind === 'insert'
                    ? `Scene ${i + 1} — product demo`
                    : `Scene ${i + 1}${scene?.line ? ` — ${scene.line}` : ''}`
                )}
              </span>
            </div>
          )
        })}
      </div>
      {at < 0 && !finished && !stopped && (
        <p className="text-xs text-ink-faint">Nothing started yet.</p>
      )}
    </div>
  )
}

function Log({ events, live }: { events: JobEvent[]; live: boolean }) {
  const ref = useRef<HTMLDivElement>(null)
  // Follow the tail while a job runs, but stop yanking the view once it has
  // finished and someone may be reading back through it.
  useEffect(() => {
    if (live && ref.current) ref.current.scrollTop = ref.current.scrollHeight
  }, [events.length, live])

  if (events.length === 0) {
    return (
      <Card className="py-8 text-center text-sm text-ink-faint">
        Nothing yet. Progress appears here once a worker picks the job up.
      </Card>
    )
  }

  return (
    <div
      ref={ref}
      className="panel max-h-80 overflow-y-auto p-3 font-mono text-[12px] leading-relaxed"
    >
      {events.map(e => (
        <div key={e.id} className="flex gap-3 py-0.5">
          <span className="w-16 shrink-0 text-ink-faint tabular-nums">
            {new Date(e.created_at).toLocaleTimeString([], { hour12: false })}
          </span>
          <span className="w-16 shrink-0 text-ink-faint">{e.stage}</span>
          <span
            className={cn(
              'min-w-0 flex-1 break-words',
              e.level === 'error' && 'text-bad',
              e.level === 'warn' && 'text-warn',
            )}
          >
            {e.message}
          </span>
        </div>
      ))}
    </div>
  )
}

function Result({ ws, storageKey, filename }: {
  ws: string
  storageKey: string
  filename: string
}) {
  const [url, setUrl] = useState('')
  useEffect(() => {
    let made = ''
    let dead = false
    blobURL(ws, storageKey)
      .then(u => { if (dead) { URL.revokeObjectURL(u); return } made = u; setUrl(u) })
      .catch(() => {})
    return () => { dead = true; if (made) URL.revokeObjectURL(made) }
  }, [ws, storageKey])

  return (
    <section>
      <div className="flex flex-wrap items-start gap-5">
        {url ? (
          <div className="space-y-3">
            <video
              src={url}
              controls
              playsInline
              className="w-full max-w-[280px] rounded-[--radius-card] border border-line bg-black shadow-[0_16px_48px_oklch(0_0_0/0.45)]"
            />
            <DownloadFileButton
              ws={ws}
              storageKey={storageKey}
              filename={filename}
              size="md"
              variant="primary"
              className="w-full max-w-[280px]"
            />
          </div>
        )
          : <Skeleton className="aspect-[9/16] w-full max-w-[280px]" />}
        <Note tone="warn">
          Watch it before shipping. Every automated gate checks words, timing, levels and
          gross visual defects — none of them judge whether the video is any good.
        </Note>
      </div>
    </section>
  )
}
